import {useCallback, useEffect, useMemo, useState} from 'react';

import {getCheckedIds} from 'src/components/features-section';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {selectDeprecatedAccessModulesMap} from 'src/hooks/deprecatedAccessModules/services/deprecated-access-modules-slice';
import {DISABLED_FOR_CHINA} from 'src/modules/bulk-update/update-license-access-modules/constants';
import {licenseFeatureModuleToTreeStructure} from 'src/modules/create-account/steps/license-create/utils';
import {getDefaultLicenseFeatureModules} from 'src/openapi/admin/apis/GetDefaultLicenseFeatureModulesApi';
import {getLicenseFeatureAccessModules} from 'src/openapi/admin/apis/GetLicenseFeatureAccessModulesApi';
import {saveLicenseFeatureAccessModules} from 'src/openapi/admin/apis/SaveLicenseFeatureAccessModulesApi';
import {useAppDispatch, useAppSelector} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import type {TreeStructure} from '@pb/kit-component-tree-v2';
import type {LicenseType} from 'src/openapi/admin/models/LicenseType';

type Props = {
    licenseId?: number,
    accountId: number,
    licenseType?: LicenseType,
    disabled?: boolean,
};

export const usePlatformFeatureModules = ({accountId, licenseId, licenseType, disabled}: Props) => {
    const apiDispatch = useAppDispatch();

    const [loading, setLoading] = useState(true);
    const [platformFeatureModulesTree, setPlatformFeatureModulesTree] = useState<TreeStructure | null>(null);
    const [defaultPlatformFeatureModules, setDefaultPlatformFeatureModules] = useState<Array<string> | null>(null);
    const [
        initialPlatformFeatureModules,
        setInitialPlatformFeatureModules,
    ] = useState<Array<string> | null>(null);
    const [platformFeatureModules, setPlatformFeatureModules] = useState<Array<string>>([]);
    const {hasAccess} = useAdminAccessRight();
    const deprecatedAccessModulesMap = useAppSelector(selectDeprecatedAccessModulesMap);

    useEffect(() => {
        let loadedDefaultPlatformFeatureModules: Array<string> = [];
        const loadPlatformFeatureModules = async (licenseTypeCode: LicenseType) => {
            setLoading(true);
            const defaultPlatformFeatureModulesResponse = await apiDispatch(getDefaultLicenseFeatureModules({
                licenseTypeCode,
                accountId,
            }));
            if (defaultPlatformFeatureModulesResponse.payload.success) {
                const tree = licenseFeatureModuleToTreeStructure(
                    defaultPlatformFeatureModulesResponse.payload.data,
                    disabled,
                );
                setPlatformFeatureModulesTree(tree);
                loadedDefaultPlatformFeatureModules = getCheckedIds(defaultPlatformFeatureModulesResponse.payload.data);
                setDefaultPlatformFeatureModules(loadedDefaultPlatformFeatureModules);
            }

            if (licenseId) {
                const platformFeatureModulesResponse = await apiDispatch(getLicenseFeatureAccessModules({licenseId}));
                if (platformFeatureModulesResponse.payload.success) {
                    setInitialPlatformFeatureModules(platformFeatureModulesResponse.payload.data.map(({code}) => code));
                    setPlatformFeatureModules(platformFeatureModulesResponse.payload.data.map(({code}) => code));
                }
            } else {
                setInitialPlatformFeatureModules(loadedDefaultPlatformFeatureModules);
                setPlatformFeatureModules(loadedDefaultPlatformFeatureModules);
            }
            setLoading(false);
        };

        if (licenseType) {
            loadPlatformFeatureModules(licenseType);
        }
    }, [licenseId, accountId, licenseType, disabled, apiDispatch]);

    const onSavePlatformFeatureModules = useCallback(async (createdLicenseId = licenseId) => {
        let error: string | null = null;

        if (createdLicenseId) {
            const requestBody = platformFeatureModules.filter(module => !(deprecatedAccessModulesMap?.[module]));

            const savePlatformFeatureModulesResponse = await apiDispatch(saveLicenseFeatureAccessModules({
                licenseId: createdLicenseId,
                requestBody,
            }));

            if (savePlatformFeatureModulesResponse.payload.error) {
                error = getError({
                    error: savePlatformFeatureModulesResponse.payload.data,
                    separator: ' ',
                });
            } else {
                setInitialPlatformFeatureModules(platformFeatureModules);
            }
        }

        return error;
    }, [licenseId, platformFeatureModules, apiDispatch, deprecatedAccessModulesMap]);

    const onDiscardPlatformFeatureModules = useCallback(() => {
        if (initialPlatformFeatureModules) {
            setPlatformFeatureModules(initialPlatformFeatureModules);
        }
    }, [initialPlatformFeatureModules]);

    const onResetPlatformFeatureModules = useCallback(() => {
        if (defaultPlatformFeatureModules) {
            setPlatformFeatureModules(prevState => {
                // TODO: please remove this dirty workaround after removal of Chinese modules
                if (hasAccess(ACCESS_RIGHTS.TEMPORARY_RESTRICTION_CHINA)) {
                    return defaultPlatformFeatureModules;
                } else {
                    return defaultPlatformFeatureModules.filter(module => {
                        if (DISABLED_FOR_CHINA.includes(module)) {
                            return prevState.includes(module);
                        } else {
                            return true;
                        }
                    });
                }
            });
        }
    }, [defaultPlatformFeatureModules, hasAccess]);

    const isDefaultPlatformFeatureModules = useMemo(() => (defaultPlatformFeatureModules
        ? isDeepEqual({prev: defaultPlatformFeatureModules, current: platformFeatureModules})
        : true
    ), [defaultPlatformFeatureModules, platformFeatureModules]);

    const isInitialPlatformFeatureModules = useMemo(() => (initialPlatformFeatureModules
        ? isDeepEqual({prev: initialPlatformFeatureModules, current: platformFeatureModules})
        : true
    ), [initialPlatformFeatureModules, platformFeatureModules]);

    return {
        loading,
        platformFeatureModulesTree,
        platformFeatureModules,
        isInitialPlatformFeatureModules,
        isDefaultPlatformFeatureModules,
        setPlatformFeatureModules,
        onSavePlatformFeatureModules,
        onDiscardPlatformFeatureModules,
        onResetPlatformFeatureModules,
    };
};
